// src/pages/documentation/gestion/GestionIndex.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCog,
    faTags,
    faUsers,
    faBox,
    faChartLine,
    faArrowRight,
    faCheckCircle,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const GestionIndex = () => {
    const sections = [
        {
            id: 'marcas-tiendas',
            title: 'Gestión de Marcas y Tiendas',
            description: 'Administra tus marcas, configura tu identidad visual, gestiona tus tiendas y herramientas de captación de clientes',
            icon: faTags,
            path: '/documentation/gestion/marcas-tiendas',
            color: 'blue',
            features: [
                'Crear y gestionar marcas',
                'Configurar identidad visual',
                'Gestionar tiendas físicas',
                'Configurar TPV y herramientas de captación'
            ]
        },
        {
            id: 'clientes',
            title: 'Gestión de Clientes',
            description: 'Administra tu base de clientes, visualiza su información y gestiona sus puntos y recompensas',
            icon: faUsers,
            path: '/documentation/gestion/clientes',
            color: 'green',
            features: [
                'Ver y buscar clientes',
                'Gestionar puntos y recompensas',
                'Historial de compras',
                'Segmentación de clientes'
            ]
        },
        {
            id: 'productos',
            title: 'Gestión de Productos',
            description: 'Administra tu catálogo de productos, categorías y precios para tu sistema de fidelización',
            icon: faBox,
            path: '/documentation/gestion/productos',
            color: 'purple',
            features: [
                'Crear y editar productos',
                'Gestionar categorías',
                'Configurar precios y descuentos',
                'Control de inventario'
            ]
        },
        {
            id: 'ventas',
            title: 'Gestión de Ventas',
            description: 'Registra y gestiona tus ventas, visualiza estadísticas y genera reportes de tu negocio',
            icon: faChartLine,
            path: '/documentation/gestion/ventas',
            color: 'orange',
            features: [
                'Registrar ventas manualmente',
                'Ver historial de ventas',
                'Generar reportes',
                'Estadísticas de ventas'
            ]
        }
    ];

    const colorClasses = {
        blue: {
            bg: 'bg-blue-100',
            text: 'text-blue-600',
            border: 'border-blue-200',
            button: 'bg-blue-600 hover:bg-blue-700'
        },
        green: {
            bg: 'bg-green-100',
            text: 'text-green-600',
            border: 'border-green-200',
            button: 'bg-green-600 hover:bg-green-700'
        },
        purple: {
            bg: 'bg-purple-100',
            text: 'text-purple-600',
            border: 'border-purple-200',
            button: 'bg-purple-600 hover:bg-purple-700'
        },
        orange: {
            bg: 'bg-orange-100',
            text: 'text-orange-600',
            border: 'border-orange-200',
            button: 'bg-orange-600 hover:bg-orange-700'
        }
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                            <FontAwesomeIcon icon={faCog} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Gestión de tu Negocio</h1>
                            <p className="text-gray-600">Aprende a administrar todos los aspectos de tu negocio en PetitPrime</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Gestión</span>
                    </div>
                </div>

                {/* Overview */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-indigo-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué encontrarás en esta sección?</h3>
                            <p className="text-gray-700 mb-3">
                                La sección de Gestión te permite administrar todos los aspectos operativos de tu negocio en PetitPrime. 
                                Desde configurar tus marcas y tiendas hasta gestionar clientes, productos y ventas.
                            </p>
                            <p className="text-gray-700">
                                Cada sección incluye guías paso a paso con capturas de pantalla y ejemplos prácticos para ayudarte 
                                a aprovechar al máximo todas las funcionalidades disponibles.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Sections Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {sections.map((section) => (
                        <div
                            key={section.id}
                            className={`bg-white border-2 ${colorClasses[section.color]?.border || 'border-gray-200'} rounded-xl p-6 hover:shadow-lg transition-all`}
                        >
                            <div className={`inline-flex p-3 ${colorClasses[section.color]?.bg || 'bg-gray-100'} rounded-lg mb-4`}>
                                <FontAwesomeIcon
                                    icon={section.icon}
                                    className={`${colorClasses[section.color]?.text || 'text-gray-600'} text-2xl`}
                                />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">{section.title}</h3>
                            <p className="text-gray-600 text-sm mb-4">{section.description}</p>
                            
                            {section.features && (
                                <ul className="text-sm text-gray-600 space-y-2 mb-4">
                                    {section.features.map((feature, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <FontAwesomeIcon icon={faCheckCircle} className={`${colorClasses[section.color]?.text || 'text-gray-400'} text-xs mt-1 flex-shrink-0`} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            
                            <Link
                                to={section.path}
                                className={`inline-flex items-center gap-2 ${colorClasses[section.color]?.button || 'bg-gray-600'} text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium`}
                            >
                                Ver Guía
                                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                            </Link>
                        </div>
                    ))}
                </div>

                {/* Quick Links */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Enlaces Rápidos</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Link
                            to="/documentation/gestion/marcas-tiendas"
                            className="flex items-center gap-3 p-3 bg-white rounded-lg hover:shadow-md transition-shadow border border-gray-200"
                        >
                            <FontAwesomeIcon icon={faTags} className="text-blue-600" />
                            <div>
                                <h4 className="font-medium text-gray-800">Gestión de Marcas y Tiendas</h4>
                                <p className="text-xs text-gray-500">Configura tu identidad y ubicaciones</p>
                            </div>
                        </Link>
                        <Link
                            to="/documentation/gestion/clientes"
                            className="flex items-center gap-3 p-3 bg-white rounded-lg hover:shadow-md transition-shadow border border-gray-200"
                        >
                            <FontAwesomeIcon icon={faUsers} className="text-green-600" />
                            <div>
                                <h4 className="font-medium text-gray-800">Gestión de Clientes</h4>
                                <p className="text-xs text-gray-500">Administra tu base de clientes</p>
                            </div>
                        </Link>
                        <Link
                            to="/documentation/gestion/productos"
                            className="flex items-center gap-3 p-3 bg-white rounded-lg hover:shadow-md transition-shadow border border-gray-200"
                        >
                            <FontAwesomeIcon icon={faBox} className="text-purple-600" />
                            <div>
                                <h4 className="font-medium text-gray-800">Gestión de Productos</h4>
                                <p className="text-xs text-gray-500">Administra tu catálogo</p>
                            </div>
                        </Link>
                        <Link
                            to="/documentation/gestion/ventas"
                            className="flex items-center gap-3 p-3 bg-white rounded-lg hover:shadow-md transition-shadow border border-gray-200"
                        >
                            <FontAwesomeIcon icon={faChartLine} className="text-orange-600" />
                            <div>
                                <h4 className="font-medium text-gray-800">Gestión de Ventas</h4>
                                <p className="text-xs text-gray-500">Importa y analiza ventas</p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default GestionIndex;

